import { createSelector } from 'reselect';

import { assignedEventsSelector, visibleEventsSelector } from '../redux/events';
import { standardCrewsSelector } from '../redux/standardCrews';
import { matchCrews } from '../utils';

const unassignedCrewArray = (includeUnassigned, active) => includeUnassigned
  ? [{ active, crew: null, events: [] }]
  : [];

function sortCrews(a, b) {
  if (a === b) {
    return 0;
  } else if (!a) {
    return -1;
  } else if (!b) {
    return 1;
  } else {
    return sortCrewsByStandardCrew(a, b) || sortCrewsByEquipment(a, b);
  }
}

function sortCrewsByEquipment(a, b) {
  if (!a.equipment && !b.equipment) {
    return 0;
  } else if (!a.equipment) {
    return -1;
  } else if (!b.equipment) {
    return 1;
  } else {
    return sortEquipment(a.equipment, b.equipment);
  }
}

function sortCrewsByStandardCrew(a, b) {
  if (a.isStandardCrew === b.isStandardCrew) {
    return 0;
  } else if (a.isStandardCrew) {
    return -1;
  } else {
    // b.isStandardCrew = true
    return 1;
  }
}

function sortEquipment(a, b) {
  return sortEquipmentByDisplayOrder(a, b) || sortEquipmentByName(a, b);
}

function sortEquipmentByDisplayOrder(a, b) {
  if (!a.displayOrder && !b.displayOrder) {
    return 0;
  } else if (typeof(a.displayOrder) !== 'number') {
    return -1;
  } else if (typeof(b.displayOrder) !== 'number') {
    return 1;
  } else {
    return a.displayOrder - b.displayOrder;
  }
}

function sortEquipmentByName(a, b) {
  return a.name < b.name
    ? -1
    : a.name > b.name
      ? 1
      : 0;
}

/**
 * Determines the largest subset of offices common to all resources on a specified crew.
 *
 * @param {any} crew
 * @returns Array of common offices
 */
function maximalOfficeSet(crew) {
  return crew.resources
    .filter(r => !!r.resource)
    .map(r => r.resource.offices)
    .reduce((prev, curr) => {
      if (!prev) {
        return curr;
      } else {
        return prev.filter(o => curr.some(o2 => o2.id === o.id));
      }
    }, null) || [];
}

export default (includeUnassigned) => {
  const distinctStandardCrews = createSelector(
    standardCrewsSelector,
    (state) => state.settings.offices,
    (crews, offices) => {
      // Filter standard crews based on selected office filter
      const filteredCrews = offices!=null ? offices.length > 0
        ? crews.filter((c) => !c.office || offices.some((o) => o.id === c.office.id))
        : crews:crews;

      // Eliminate any duplicate standard crews based on equipment and resources
      // Map to include offices common to all resources to determine eligible events to be scheduled
      return filteredCrews
        .reduce((distinct, crew) => {
          if (distinct.some(c => matchCrews(c, crew))) {
            return distinct;
          } else {
            return [...distinct, crew];
          }
        }, [])
        .map(c => ({ ...c, isStandardCrew: true, offices: maximalOfficeSet(c) }));
    }
  );

  const visibleEvents = createSelector(
    includeUnassigned ? visibleEventsSelector : assignedEventsSelector,
    events => events.map(e => ({
      ...e,
      crew: e.crew ? {
        ...e.crew,
        offices: maximalOfficeSet(e.crew)
      } : null
    }))
  );

  return createSelector(
    visibleEvents,
    distinctStandardCrews,
    (state) => state.calendar.activeCrew,
    (events, standardCrews, activeCrew) => events
      .reduce((crews, event) => {
        let crew = crews.find((c) => matchCrews(c.crew, event.crew));
        if (!crew) {
          crew = {
            active: !!activeCrew && activeCrew.id === event.crew.id,
            crew: event.crew,
            events: []
          };
          crews.push(crew);
        }
        crew.events.push(event);
        return crews;
      }, [
        ...unassignedCrewArray(includeUnassigned, !activeCrew),
        ...standardCrews.map((c) => ({ active: !!activeCrew && activeCrew.id === c.id, crew: c, events: [] }))
      ])
      .sort((a, b) => sortCrews(a.crew, b.crew))
  );
};
